import { IMAGE_JPEG_QUALITY, IMAGE_WEBP_QUALITY } from './imagePresets';
import { sanitize_theme_logo_data_uri } from './sanitizeThemeLogoSvg';

const MIN_JPEG_QUALITY = 0.5;
const QUALITY_STEP = 0.05;
const DIMENSION_SHRINK_FACTOR = 0.9;
const MAX_OPTIMIZATION_ITERATIONS = 24;
const ALPHA_SAMPLE_STRIDE = 40;

export function dataUrlByteSize(dataUrl) {
    const base64 = (dataUrl || '').split(',')[1];
    if (!base64)
        return 0;
    return Math.ceil((base64.length * 3) / 4);
}

export function formatImageBytes(bytes) {
    const value = Math.max(0, bytes || 0);
    if (value < 1024)
        return `${value} o`;
    if (value < 1024 * 1024)
        return `${Math.round(value / 1024)} Ko`;
    return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
}

export function fitInBox(width, height, maxWidth, maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
    return {
        width: Math.round(width * ratio),
        height: Math.round(height * ratio),
    };
}

function canvasHasAlpha(ctx, width, height) {
    const { data } = ctx.getImageData(0, 0, width, height);
    for (let i = 3; i < data.length; i += ALPHA_SAMPLE_STRIDE) {
        if (data[i] < 255)
            return true;
    }
    return false;
}

function bitmapToCanvas(bitmap) {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    if (typeof bitmap.close === 'function')
        bitmap.close();
    return canvas;
}

function resizeCanvas(canvas, width, height) {
    if (canvas.width === width && canvas.height === height)
        return canvas;

    const resized = document.createElement('canvas');
    resized.width = width;
    resized.height = height;
    resized.getContext('2d').drawImage(canvas, 0, 0, width, height);
    return resized;
}

function encodeCanvas(canvas, mime, quality) {
    if (mime === 'image/png')
        return canvas.toDataURL('image/png');
    if (mime === 'image/webp')
        return canvas.toDataURL('image/webp', quality);
    return canvas.toDataURL('image/jpeg', quality);
}

function resolveLosslessMime(file) {
    if (file.type === 'image/jpeg' || file.type === 'image/png')
        return file.type;
    return null;
}

function resolveLossyMime(file, options, ctx, width, height) {
    if (options.outputFormat === 'webp')
        return 'image/webp';
    if (options.preservePng && file.type === 'image/png')
        return 'image/png';
    if (options.preserveAlpha && canvasHasAlpha(ctx, width, height))
        return 'image/png';
    return 'image/jpeg';
}

function mimeChanged(file, mime) {
    return Boolean(file.type && mime && file.type !== mime);
}

/**
 * Decide whether the processed payload should replace the uploaded file.
 *
 * @returns {{ useProcessed: boolean, reason: 'larger'|'negligible_gain'|null }}
 */
export function shouldUseProcessed({
    originalPayloadBytes,
    processedPayloadBytes,
    maxBytes,
    negligibleSavingsBytes,
    lossy,
}) {
    if (processedPayloadBytes >= originalPayloadBytes)
        return { useProcessed: false, reason: 'larger' };

    const savedBytes = originalPayloadBytes - processedPayloadBytes;

    if (!lossy && savedBytes > 0)
        return { useProcessed: true, reason: null };

    if (lossy && originalPayloadBytes <= maxBytes && savedBytes < negligibleSavingsBytes)
        return { useProcessed: false, reason: 'negligible_gain' };

    return { useProcessed: true, reason: null };
}

function buildKeptOriginalReport(originalDataUrl, originalWidth, originalHeight, keepReason = null) {
    const originalPayloadBytes = dataUrlByteSize(originalDataUrl);

    return {
        modified: false,
        keptOriginal: true,
        keepReason,
        lossy: false,
        losslessReencoded: false,
        resized: false,
        formatChanged: false,
        compressed: false,
        originalBytes: originalPayloadBytes,
        finalBytes: originalPayloadBytes,
        savedBytes: 0,
        originalWidth,
        originalHeight,
        finalWidth: originalWidth,
        finalHeight: originalHeight,
    };
}

function buildProcessedReport({
    originalPayloadBytes,
    processedPayloadBytes,
    originalWidth,
    originalHeight,
    canvas,
    lossy,
    actions,
}) {
    return {
        modified: true,
        keptOriginal: false,
        keepReason: null,
        lossy,
        losslessReencoded: actions.has('losslessReencoded'),
        resized: actions.has('resized'),
        formatChanged: actions.has('formatChanged'),
        compressed: actions.has('compressed'),
        originalBytes: originalPayloadBytes,
        finalBytes: processedPayloadBytes,
        savedBytes: Math.max(0, originalPayloadBytes - processedPayloadBytes),
        originalWidth,
        originalHeight,
        finalWidth: canvas.width,
        finalHeight: canvas.height,
    };
}

function tryLosslessCandidate(canvas, file) {
    const mime = resolveLosslessMime(file);
    if (!mime)
        return null;

    const quality = mime === 'image/jpeg' ? IMAGE_JPEG_QUALITY : undefined;
    const dataUrl = encodeCanvas(canvas, mime, quality);
    const actions = new Set(['losslessReencoded']);

    return {
        dataUrl,
        canvas,
        lossy: false,
        actions,
    };
}

function tryLossyCandidate(sourceCanvas, file, options) {
    let canvas = sourceCanvas;
    const actions = new Set();

    const fitted = fitInBox(
        canvas.width,
        canvas.height,
        options.maxWidth,
        options.maxHeight,
    );
    if (fitted.width !== canvas.width || fitted.height !== canvas.height) {
        canvas = resizeCanvas(canvas, fitted.width, fitted.height);
        actions.add('resized');
    }

    const ctx = canvas.getContext('2d');
    const mime = resolveLossyMime(file, options, ctx, canvas.width, canvas.height);
    if (mimeChanged(file, mime))
        actions.add('formatChanged');

    let quality = options.outputFormat === 'webp'
        ? (options.webpQuality ?? IMAGE_WEBP_QUALITY)
        : (options.jpegQuality ?? IMAGE_JPEG_QUALITY);
    let dataUrl = encodeCanvas(canvas, mime, quality);
    let iterations = 0;

    while (dataUrlByteSize(dataUrl) > options.maxBytes && iterations < MAX_OPTIMIZATION_ITERATIONS) {
        if ((mime === 'image/jpeg' || mime === 'image/webp') && quality > MIN_JPEG_QUALITY) {
            quality = Math.max(MIN_JPEG_QUALITY, quality - QUALITY_STEP);
            actions.add('compressed');
        }
        else {
            const shrunk = fitInBox(
                canvas.width,
                canvas.height,
                Math.max(1, Math.floor(canvas.width * DIMENSION_SHRINK_FACTOR)),
                Math.max(1, Math.floor(canvas.height * DIMENSION_SHRINK_FACTOR)),
            );
            canvas = resizeCanvas(canvas, shrunk.width, shrunk.height);
            actions.add('resized');
        }

        dataUrl = encodeCanvas(canvas, mime, quality);
        iterations += 1;
    }

    return {
        dataUrl,
        canvas,
        lossy: true,
        actions,
    };
}

/**
 * When a preset sets outputFormat (e.g. webp), try converting jpeg/png even if
 * the image already fits size/dimension limits. Skip when the file is already
 * in the target format to avoid generation loss (webp → webp).
 */
export function wantsTargetFormatConversion(file, options) {
    if (!options?.outputFormat || !file?.type)
        return false;

    const targetMime = `image/${options.outputFormat}`;
    return file.type !== targetMime;
}

export function needsLossyPipeline(originalPayloadBytes, canvas, options, losslessCandidate, file = null) {
    if (canvas.width > options.maxWidth || canvas.height > options.maxHeight)
        return true;
    if (originalPayloadBytes > options.maxBytes)
        return true;
    if (losslessCandidate && dataUrlByteSize(losslessCandidate.dataUrl) > options.maxBytes)
        return true;
    if (wantsTargetFormatConversion(file, options))
        return true;
    return false;
}

function selectBestCandidate(originalPayloadBytes, candidates, options) {
    let best = null;
    let bestBytes = Infinity;

    for (const candidate of candidates) {
        if (!candidate?.dataUrl)
            continue;

        const processedBytes = dataUrlByteSize(candidate.dataUrl);
        const decision = shouldUseProcessed({
            originalPayloadBytes,
            processedPayloadBytes: processedBytes,
            maxBytes: options.maxBytes,
            negligibleSavingsBytes: options.negligibleSavingsBytes,
            lossy: candidate.lossy,
        });

        if (!decision.useProcessed || processedBytes >= bestBytes)
            continue;

        bestBytes = processedBytes;
        best = { ...candidate, processedBytes, decision };
    }

    return best;
}

function resolveKeepReason(originalPayloadBytes, candidates, options) {
    const lossyCandidate = candidates.find(candidate => candidate?.lossy);
    if (!lossyCandidate)
        return 'larger';

    const decision = shouldUseProcessed({
        originalPayloadBytes,
        processedPayloadBytes: dataUrlByteSize(lossyCandidate.dataUrl),
        maxBytes: options.maxBytes,
        negligibleSavingsBytes: options.negligibleSavingsBytes,
        lossy: true,
    });

    return decision.reason || 'larger';
}

async function loadBitmap(file) {
    if (typeof createImageBitmap !== 'function')
        throw new Error('createImageBitmap is not available');

    return createImageBitmap(file, { imageOrientation: 'from-image' });
}

export async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(file);
    });
}

function is_svg_upload(file) {
    if (!file)
        return false;
    if (file.type === 'image/svg+xml')
        return true;
    return /\.svg$/i.test(file.name || '');
}

/**
 * @param {File} file
 * @param {object} options from getImagePresetOptions
 * @returns {Promise<{ dataUrl: string, report: object }>}
 */
export async function processImageFile(file, options) {
    const originalDataUrl = await fileToDataUrl(file);
    const originalPayloadBytes = dataUrlByteSize(originalDataUrl);

    // Theme logos must stay SVG: the public app only renders svg+xml via SvgXml,
    // and canvas→WebP turns fill-less paths into a fully transparent bitmap.
    if (options.preserveSvg && is_svg_upload(file)) {
        const dataUrl = sanitize_theme_logo_data_uri(originalDataUrl) || originalDataUrl;
        return {
            dataUrl,
            report: buildKeptOriginalReport(dataUrl, 0, 0, 'preserve_svg'),
        };
    }

    const bitmap = await loadBitmap(file);
    const originalWidth = bitmap.width;
    const originalHeight = bitmap.height;
    const sourceCanvas = bitmapToCanvas(bitmap);

    if (options.forceProcess) {
        const lossy = tryLossyCandidate(sourceCanvas, file, options);
        return {
            dataUrl: lossy.dataUrl,
            report: buildProcessedReport({
                originalPayloadBytes,
                processedPayloadBytes: dataUrlByteSize(lossy.dataUrl),
                originalWidth,
                originalHeight,
                canvas: lossy.canvas,
                lossy: true,
                actions: lossy.actions,
            }),
        };
    }

    const candidates = [];
    const losslessCandidate = tryLosslessCandidate(sourceCanvas, file);
    if (losslessCandidate)
        candidates.push(losslessCandidate);

    if (needsLossyPipeline(originalPayloadBytes, sourceCanvas, options, losslessCandidate, file))
        candidates.push(tryLossyCandidate(sourceCanvas, file, options));

    const best = selectBestCandidate(originalPayloadBytes, candidates, options);

    if (!best) {
        return {
            dataUrl: originalDataUrl,
            report: buildKeptOriginalReport(
                originalDataUrl,
                originalWidth,
                originalHeight,
                resolveKeepReason(originalPayloadBytes, candidates, options),
            ),
        };
    }

    return {
        dataUrl: best.dataUrl,
        report: buildProcessedReport({
            originalPayloadBytes,
            processedPayloadBytes: best.processedBytes,
            originalWidth,
            originalHeight,
            canvas: best.canvas,
            lossy: best.lossy,
            actions: best.actions,
        }),
    };
}
